import { Capacitor, registerPlugin } from '@capacitor/core';

const LoopsLocation = registerPlugin('LoopsLocation');

let isTrackingActive = false;
let webWatchId = null;
let pluginListener = null;

/**
 * Démarre le suivi de position d'arrière-plan.
 * Sur Android (Capacitor), active le Foreground Service avec notification permanente.
 * Sur Web / Desktop, bascule sur navigator.geolocation.watchPosition.
 */
export async function startBackgroundTracking({ jobId, driverId, truckCode, onLocationUpdate }) {
  if (isTrackingActive) {
    console.log('[TrackerService] Tracking already active.');
    return;
  }

  isTrackingActive = true;
  const serverUrl = window.location.origin;

  if (Capacitor.isNativePlatform()) {
    try {
      console.log('[TrackerService] Starting native Capacitor background tracking for job:', jobId);
      
      // Register location listener if callback provided
      if (onLocationUpdate && !pluginListener) {
        pluginListener = await LoopsLocation.addListener('locationUpdate', (location) => {
          console.log('[TrackerService] Native location update received:', location);
          onLocationUpdate(location);
        });
      }

      const res = await LoopsLocation.startTracking({
        jobId: String(jobId || ''),
        driverId: String(driverId || ''),
        truckCode: String(truckCode || ''),
        serverUrl: serverUrl
      });
      console.log('[TrackerService] Native service response:', res);
    } catch (err) {
      console.error('[TrackerService] Failed to start native tracking:', err);
    }
  } else {
    // Web Fallback
    console.log('[TrackerService] Native platform not detected; using Web Geolocation fallback.');
    if ('geolocation' in navigator) {
      webWatchId = navigator.geolocation.watchPosition(
        async (position) => {
          const payload = {
            job_id: jobId,
            driver_id: driverId,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            speed: position.coords.speed ? position.coords.speed * 3.6 : 0,
            heading: position.coords.heading || 0,
            accuracy: position.coords.accuracy || 0,
            recorded_at: new Date(position.timestamp).toISOString()
          };

          if (onLocationUpdate) {
            onLocationUpdate(payload);
          }

          try {
            await fetch('/api/driver-geohistory', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
            });
          } catch (e) {
            console.warn('[TrackerService] Web fallback failed to post coordinates:', e);
          }
        },
        (error) => {
          console.warn('[TrackerService] Geolocation error:', error.message);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 5000,
          timeout: 10000
        }
      );
    }
  }
}

/**
 * Arrête le suivi d'arrière-plan et ferme la notification permanente.
 */
export async function stopBackgroundTracking() {
  if (!isTrackingActive) return;

  isTrackingActive = false;

  if (Capacitor.isNativePlatform()) {
    try {
      console.log('[TrackerService] Stopping native Capacitor background tracking');
      if (pluginListener) {
        await pluginListener.remove();
        pluginListener = null;
      }
      await LoopsLocation.stopTracking();
    } catch (err) {
      console.warn('[TrackerService] Error stopping native tracking:', err);
    }
  } else {
    if (webWatchId !== null && 'geolocation' in navigator) {
      navigator.geolocation.clearWatch(webWatchId);
      webWatchId = null;
    }
  }
}

/**
 * Vérifie si le suivi est actuellement actif.
 */
export async function isTrackingRunning() {
  if (Capacitor.isNativePlatform()) {
    try {
      const res = await LoopsLocation.isTracking();
      return !!res?.running;
    } catch (e) {
      return isTrackingActive;
    }
  }
  return isTrackingActive;
}
